import React, { useMemo } from "react";
import { useField } from "formik";
import { FormField, Label } from "semantic-ui-react";
import { Editor } from "@tinymce/tinymce-react";

export default function MyTextInput({ label, helperText, helperTextStyle, tokenButtons, ...props }) {
    const [field, meta, helpers] = useField(props);
    // console.log('tiny', "https://bms.igotravel.co.za/tinymce/tinymce.min.js");

    const tokenButtonList = useMemo(() => {
        if (!Array.isArray(tokenButtons) || tokenButtons.length === 0) return [];
        // Expected shape: { name: "insert_terms_link", text: "Insert Terms Link", token: "{{TERMS_LINK}}" }
        return tokenButtons.filter((b) => b && b.name && b.text && b.token);
    }, [tokenButtons]);

    const toolbar = useMemo(() => {
        const base =
            "undo redo | styles | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | table | code | clearall";
        if (tokenButtonList.length === 0) return base;
        return `${base} | ${tokenButtonList.map((b) => b.name).join(" ")}`;
    }, [tokenButtonList]);

    return (
        <FormField error={meta.touched && !!meta.error}>
            {label && (
                <label>
                    <strong>{label}</strong>
                </label>
            )}
            {helperText && (
                <div
                    style={{
                        marginTop: 6,
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 12,
                        lineHeight: "16px",
                        ...(helperTextStyle || {}),
                    }}
                >
                    {helperText}
                </div>
            )}

            <Editor
                value={field.value || ""}
                init={{
                    tinymceScriptSrc: "https://bms.igotravel.co.za/tinymce/tinymce.min.js",
                    license_key: 'gpl',
                    plugins: "lists link code autoresize table",
                    toolbar,
                    font_formats:
                        "Calibri=Calibri,Verdana,Geneva,sans-serif;Arial=arial,helvetica,sans-serif;Courier New=courier new,courier,monospace",
                    style_formats: [
                        { title: "Heading 1", block: "h1" },
                        { title: "Heading 2", block: "h2" },
                        { title: "Heading 3", block: "h3" },
                        { title: "Heading 4", block: "h4" },
                        { title: "Heading 5", block: "h5" },
                        { title: "Heading 6", block: "h6" },
                        { title: "Paragraph", block: "p" },
                        {
                            title: "Arial",
                            inline: "span",
                            styles: { "font-family": "Arial" },
                        },
                        {
                            title: "Montserrat",
                            inline: "span",
                            styles: { "font-family": "Montserrat, sans-serif" },
                        },
                        {
                            title: "Courier New",
                            inline: "span",
                            styles: { "font-family": '"Courier New"' },
                        },
                        {
                            title: "Small text",
                            inline: "span",
                            styles: { "font-size": "12px" },
                        },
                        {
                            title: "Medium text",
                            inline: "span",
                            styles: { "font-size": "18px" },
                        },
                        {
                            title: "Large text",
                            inline: "span",
                            styles: { "font-size": "24px" },
                        },
                    ],
                    browser_spellcheck: true,
                    branding: false,
                    menubar: false,
                    contextmenu: false,
                    relative_urls: false,
                    remove_script_host: false,
                    height: 300,
                    autoresize_bottom_margin: 100,
                    text_patterns: false,
                    paste_postprocess: (plugin, args) => {
                        const tables = args.node.querySelectorAll("table");
                        console.log(tables);
                        // remove all style attributes from all tables
                        tables.forEach((table) => {
                            table.removeAttribute("style");
                            table.removeAttribute("class");
                            table.removeAttribute("border");
                            table.removeAttribute("id");

                            // remove style attributes from all rows and cells
                            table.querySelectorAll("tr, td, th, p, span").forEach((el) => {
                                el.removeAttribute("class");
                                el.removeAttribute("style");
                                el.removeAttribute("id");
                            });

                            table.querySelectorAll("tr").forEach((row, i) => {
                                const cells = row.querySelectorAll("td, th"); // target header or data cells

                                cells.forEach((cell) => {
                                    if (i === 0) {
                                        // Header row styling
                                        cell.style.paddingBottom = "8px";
                                        cell.style.fontFamily = "Arial, sans-serif";
                                        cell.style.fontSize = "12px";
                                        cell.style.fontWeight = "bold";
                                    } else {
                                        // Data row styling
                                        cell.style.paddingBottom = "4px";
                                        cell.style.fontFamily = "Arial, sans-serif";
                                        cell.style.fontSize = "12px";
                                    }
                                });
                            });
                            // Apply consistent padding and font to each cell
                            // table.querySelectorAll("td, th").forEach((cell, i) => {
                            //     console.log('index', i);
                            //     console.log('cell', cell);
                            //     cell.style.padding = "2px";
                            //     cell.style.fontFamily = "Arial, sans-serif";
                            //     cell.style.fontSize = "12px";
                            //   });
                        });
                    },
                    setup: (editor) => {
                        // editor.on("init", function () {
                        // console.log(
                        //     `TinyMCE initialized with spellcheck: ${spellcheckEnabled}, dark mode: ${darkModeEnabled}`
                        // );
                        // });
                        editor.on('keydown', function (e) {
                            if (e.key === 'Tab') {
                                e.preventDefault(); // prevent default TinyMCE behavior

                                if (e.shiftKey) {
                                    // Shift + Tab: your custom behavior
                                    editor.execCommand('mceInsertContent', false, '&nbsp;&nbsp;&nbsp;&nbsp;'); // insert spaces
                                } else {
                                    // Tab: normal behavior (or another custom one)
                                    editor.execCommand('Indent'); // TinyMCE's normal indent
                                }
                            }
                        });
                        editor.ui.registry.addButton('clearall', {
                            text: 'Clear Formatting',
                            onAction: function () {
                                let text = editor.getContent({ format: 'text' }); // get plain text
                                // Replace non-breaking spaces with normal spaces
                                text = text.replace(/\u00A0/g, ' ');

                                // Collapse multiple spaces/tabs into a single space
                                text = text.replace(/\s+/g, ' ');

                                // Trim leading/trailing whitespace
                                text = text.trim();
                                editor.setContent(text); // replace content with plain text only
                            }
                        });

                        // Optional per-instance token insert buttons (used for email templates).
                        tokenButtonList.forEach((btn) => {
                            editor.ui.registry.addButton(btn.name, {
                                text: btn.text,
                                onAction: function () {
                                    editor.execCommand("mceInsertContent", false, btn.token);
                                },
                            });
                        });

                        editor.on("PastePostProcess", () => {
                            setTimeout(() => {
                                console.log('PastePostProcess');
                                const editorBody = editor.getBody();
                                const tables = editorBody.querySelectorAll("table");
                                if (tables.length > 0) {
                                    const paragraphs = editorBody.querySelectorAll("p");
                                    console.log('paragraphs ', paragraphs);
                                    paragraphs.forEach((p, i) => {
                                        const isEmpty = p.innerHTML.trim() === "" || p.innerHTML === "&nbsp;" || p.innerHTML === "<br data-mce-bogus=\"1\">";
                                        //const isInsideTable = p.closest("table") !== null;&& !isInsideTable
                                        console.log('isEmpty', isEmpty);
                                        // 👇 Instead of checking nextSibling, check if this is the LAST <p>
                                        const isLast = i === paragraphs.length - 1;
                                        console.log('isLast', isLast);
                                        if (isEmpty && isLast) {
                                            p.remove();
                                            console.log("Removed trailing empty <p>");
                                        }
                                    });
                                }
                            }, 0);
                        });
                    },
                }}
                onEditorChange={(content) => {
                    helpers.setValue(content);
                }}
                onBlur={() => helpers.setTouched(true)}
            />

            {meta.touched && meta.error && (
                <Label basic color="red">
                    {meta.error}
                </Label>
            )}
        </FormField>
    );
}
